import posthog from "posthog-js";

const reported = new Set<string>();
const MAX_REPORTED = 200;

function readPosthogIds(): { sessionId: string | null; distinctId: string | null } {
  try {
    const sessionId = posthog.get_session_id?.() ?? null;
    const distinctId = posthog.get_distinct_id?.() ?? null;
    return {
      sessionId: typeof sessionId === "string" && sessionId ? sessionId : null,
      distinctId: typeof distinctId === "string" && distinctId ? distinctId : null,
    };
  } catch {
    return { sessionId: null, distinctId: null };
  }
}

const estFormatter = typeof Intl !== "undefined"
  ? new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZoneName: "short",
    })
  : null;

function formatEst(date: Date): string {
  if (!estFormatter) return date.toISOString();
  const parts = estFormatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const tz = get("timeZoneName") || "EST";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")} ${tz}`;
}

export type ReportSource = "toast" | "render" | "window" | "unhandledrejection";

export type ReportInput = {
  source: ReportSource;
  message: string;
  stack?: string | null;
};

export function reportClientError(input: ReportInput): void {
  if (typeof window === "undefined") return;

  const message = (input.message || "").toString().trim();
  if (!message) return;

  const fingerprint = `${input.source}:${message.slice(0, 200)}`;
  if (reported.has(fingerprint)) return;
  if (reported.size >= MAX_REPORTED) reported.clear();
  reported.add(fingerprint);

  const { sessionId, distinctId } = readPosthogIds();

  const payload = {
    source: input.source,
    message,
    stack: input.stack ?? null,
    est_timestamp: formatEst(new Date()),
    path: window.location.pathname + window.location.search,
    userAgent: navigator.userAgent,
    posthog_session_id: sessionId,
    posthog_distinct_id: distinctId,
  };

  try {
    void fetch("/api/error-report", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never let error capture itself surface an error
  }
}
